import { useState, useEffect } from "react";
import {
  Container,
  Stack,
  Table,
  Group,
  Button,
  SegmentedControl,
  Switch,
  Loader,
  Text,
  Badge,
} from "@mantine/core";
import { IconThumbUp, IconThumbDown } from "@tabler/icons-react";
import { listFeedbacks } from "../api";
import { useLocation } from "wouter";
import ReactTimeago from "react-timeago-i18n";

export default function FeedbackPage() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoreFilter, setScoreFilter] = useState("all");
  const [hasTextFilter, setHasTextFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [, setLocation] = useLocation();

  const load = (overrides = {}) => {
    setLoading(true);
    const params = {
      page: overrides.page ?? page,
      pageSize: 50,
      score:
        (overrides.scoreFilter ?? scoreFilter) === "positive"
          ? 1
          : (overrides.scoreFilter ?? scoreFilter) === "negative"
            ? -1
            : undefined,
      hasText: (overrides.hasTextFilter ?? hasTextFilter) || undefined,
    };
    listFeedbacks(params).then((data) => {
      setFeedbacks(data);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
    // Inline callback recreated every render; including it would rerun this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <Container fluid>
      <Stack gap="md">
        <Text fz="xl" fw={600}>
          Feedback
        </Text>

        <Group gap="md" align="flex-end">
          <div>
            <Text size="xs" mb={4}>
              Score
            </Text>
            <SegmentedControl
              size="xs"
              value={scoreFilter}
              onChange={(val) => {
                setScoreFilter(val);
                setPage(1);
                load({ scoreFilter: val, page: 1 });
              }}
              data={[
                { label: "All", value: "all" },
                { label: "Positive", value: "positive" },
                { label: "Negative", value: "negative" },
              ]}
            />
          </div>
          <Switch
            label="With comments"
            checked={hasTextFilter}
            onChange={(e) => {
              const val = e.currentTarget.checked;
              setHasTextFilter(val);
              setPage(1);
              load({ hasTextFilter: val, page: 1 });
            }}
            size="xs"
            mt={22}
          />
          {loading && <Loader size="xs" />}
        </Group>

        <Table striped highlightOnHover style={{ cursor: "pointer" }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Time</Table.Th>
              <Table.Th w={50}>Score</Table.Th>
              <Table.Th>Flow</Table.Th>
              <Table.Th>Model</Table.Th>
              <Table.Th>Comment</Table.Th>
              <Table.Th>End User</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {feedbacks.map((f) => (
              <Table.Tr
                key={f.id}
                onClick={() => setLocation(`/logs/${f.shuttleRequestId}`)}
              >
                <Table.Td>
                  <ReactTimeago date={f.createdAt} />
                </Table.Td>
                <Table.Td>
                  {f.score === 1 && (
                    <IconThumbUp
                      size={16}
                      color="var(--mantine-color-green-6)"
                    />
                  )}
                  {f.score === -1 && (
                    <IconThumbDown
                      size={16}
                      color="var(--mantine-color-red-6)"
                    />
                  )}
                </Table.Td>
                <Table.Td>
                  {f.flowName && (
                    <Badge variant="light" size="sm" radius="xs">
                      {f.flowName}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{f.modelName}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" lineClamp={1} maw={300}>
                    {f.text}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {f.endUserId}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group>
          <Button
            variant="default"
            size="xs"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <Text size="sm">Page {page}</Text>
          <Button
            variant="default"
            size="xs"
            disabled={feedbacks.length < 50}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}
